'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

class TokenManager {
    constructor() {
    }
    parseJwt(token) {
        if (!token) {
            return undefined;
        }
        var base64Url = token.split(".")[1];
        var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        var jsonPayload = decodeURIComponent(atob(base64)
            .split("")
            .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
            .join(""));
        return JSON.parse(jsonPayload);
    }
}

class User {
    constructor(accessToken, idTokenRaw) {
        this.tokenManager = new TokenManager();
        var idToken = this.tokenManager.parseJwt(idTokenRaw !== null && idTokenRaw !== void 0 ? idTokenRaw : "");
        if (idToken) {
            this.uuid = idToken.uuid;
            this.id = idToken.id;
            this.description = idToken.description;
            this.accessToken = accessToken;
            this.status = idToken.status;
        }
    }
}

class AuthResult {
    constructor(accessToken, idToken, state, expiresAt) {
        this.accessToken = accessToken;
        this.idToken = idToken;
        this.state = state;
        this.expiresAt = expiresAt;
    }
}

class StorageService {
    constructor() {
        this.authResultKey = "pdi-auth-result";
    }
    getAccessToken() {
        var authResult = this.getAuthResult();
        return authResult === null || authResult === void 0 ? void 0 : authResult.accessToken;
    }
    getIdToken() {
        var authResult = this.getAuthResult();
        return authResult === null || authResult === void 0 ? void 0 : authResult.idToken;
    }
    getTokenExpiration() {
        var authResult = this.getAuthResult();
        return authResult === null || authResult === void 0 ? void 0 : authResult.expiresAt;
    }
    setAuthResult(authResult) {
        sessionStorage.setItem(this.authResultKey, JSON.stringify(authResult));
    }
    clearStorage() {
        sessionStorage.removeItem(this.authResultKey);
    }
    getAuthResult() {
        let authResult = sessionStorage.getItem(this.authResultKey);
        return authResult ? JSON.parse(authResult) : undefined;
    }
}

class IframeService {
    constructor() {
        this.storageService = new StorageService();
    }
    executeFramedUserCall(requestUrl, timeout, state, origin) {
        var iframePromise = this.addPostMessageListener(timeout, state, origin);
        var iframeEl = this.loadFrame(requestUrl + (state ? `&state=${state}` : ""));
        return iframePromise.then((x) => {
            var _a;
            if (document.body.contains(iframeEl)) {
                (_a = iframeEl === null || iframeEl === void 0 ? void 0 : iframeEl.parentElement) === null || _a === void 0 ? void 0 : _a.removeChild(iframeEl);
            }
            if (x) {
                this.storageService.setAuthResult(x);
            }
            return x;
        }).catch(() => {
            var _a;
            if (document.body.contains(iframeEl)) {
                (_a = iframeEl === null || iframeEl === void 0 ? void 0 : iframeEl.parentElement) === null || _a === void 0 ? void 0 : _a.removeChild(iframeEl);
            }
            return new AuthResult(undefined, undefined, undefined, undefined);
        });
    }
    addPostMessageListener(timeout, state, originUrl) {
        var responseHandler;
        var timeoutId;
        var msgReceivedOrTimeout = new Promise((resolve, reject) => {
            responseHandler = function responseHandler(e) {
                if (!e.data || e.data.state != state) {
                    // A message not meant for us
                    return;
                }
                const eOriginHost = new URL(e.origin);
                const originUrlHost = new URL(originUrl !== null && originUrl !== void 0 ? originUrl : "");
                if ((eOriginHost.hostname + eOriginHost.pathname) !== (originUrlHost.hostname + originUrlHost.pathname)) {
                    return reject('The request does not match client configuration');
                }
                resolve(new AuthResult(e.data.accessToken, e.data.idToken, e.data.state, e.data.expiresAt));
            };
            this.addListener(window, "message", responseHandler);
            timeoutId = setTimeout(() => {
                reject("OAuth flow timed out.");
            }, timeout || 120000);
        });
        return msgReceivedOrTimeout.then((x) => {
            clearTimeout(timeoutId);
            this.removeListener(window, "message", responseHandler);
            return x;
        });
    }
    addListener(eventTarget, name, fn) {
        if (eventTarget.addEventListener) {
            eventTarget.addEventListener(name, fn);
        }
        else {
            eventTarget.attachEvent("on" + name, fn);
        }
    }
    removeListener(eventTarget, name, fn) {
        if (eventTarget.removeEventListener) {
            eventTarget.removeEventListener(name, fn);
        }
        else {
            eventTarget.detachEvent("on" + name, fn);
        }
    }
    loadFrame(src) {
        var iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = src;
        return document.body.appendChild(iframe);
    }
}

class AuthenticationService {
    constructor(authenticationSiteUrl) {
        this.iframeService = new IframeService();
        this.storageService = new StorageService();
        this.authenticationSiteUrl = authenticationSiteUrl;
        this.startTokenExpirationCheck();
    }
    authenticate(callbackUrl) {
        return this.isUserAuthenticated().then((x) => {
            if (!x.accessToken) {
                this.redirectToLoginSite(callbackUrl);
            }
            return new User(x.accessToken, x.idToken);
        });
    }
    getUser() {
        let accessToken = this.storageService.getAccessToken();
        let idToken = this.storageService.getIdToken();
        return accessToken ? new User(accessToken, idToken) : undefined;
    }
    getAccessToken() {
        return this.storageService.getAccessToken();
    }
    getTokenExpiration() {
        return this.storageService.getTokenExpiration();
    }
    logOut(callbackUrl) {
        this.storageService.clearStorage();
        let absoluteCallbackUrl = callbackUrl ? callbackUrl : window.location.origin;
        window.location.assign(`${this.authenticationSiteUrl}/logout?origin=${encodeURIComponent(absoluteCallbackUrl)}`);
    }
    isUserAuthenticated() {
        return this.iframeService.executeFramedUserCall(`${this.authenticationSiteUrl}/user?origin=${encodeURIComponent(window.location.origin)}`, undefined, this.getUuidv4(), this.authenticationSiteUrl);
    }
    redirectToLoginSite(callbackUrl) {
        let absoluteCallbackUrl = callbackUrl ? callbackUrl : window.location.origin;
        let redirectUrl = `${this.authenticationSiteUrl}/login?origin=${encodeURIComponent(absoluteCallbackUrl)}`;
        window.location.assign(redirectUrl);
    }
    startTokenExpirationCheck() {
        setInterval(() => {
            var _a;
            let tokenExpiresAt = (_a = this.storageService.getTokenExpiration()) !== null && _a !== void 0 ? _a : undefined;
            if (tokenExpiresAt && tokenExpiresAt < Date.now() / 1000) {
                this.storageService.clearStorage();
                this.isUserAuthenticated().then(() => console.log("renewed"));
            }
        }, 1000);
    }
    getUuidv4() {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            var r = (Math.random() * 16) | 0, v = c == "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}

exports.AuthenticationService = AuthenticationService;
